import { apiClient } from '@/core/api/client';

export type ShareContactCardRequest = {
  /**
   * ServiceProvider profile id — the same id `/providers/:id` uses, which is what
   * `useProviderBase` and provider search both expose as `provider.id`.
   * Not the provider's User id.
   */
  serviceProviderId: string;
  recipientEmail: string;
  recipientFirstName: string;
};

export type ShareContactCardResponse = {
  /** Correlation id for the send; also travels on the links in the email. */
  deliveryId: string;
};

/**
 * Sends a Service Provider's contact card to someone with no bQuest account.
 *
 * Two routes, one per sending role, because each is guarded differently: the
 * advisor route checks the FINANCIAL_ADVISOR role, the provider route scopes
 * `:senderUserId` through the workspace owner. Posting to the wrong one is a
 * 403, so the caller has to pick by role rather than by convenience.
 *
 * A 200 means SendGrid accepted the message — the endpoints deliberately fail
 * rather than reporting a false success when delivery is rejected.
 */
export async function shareContactCardAsAdvisor(
  data: ShareContactCardRequest,
): Promise<ShareContactCardResponse> {
  const response = await apiClient.post<ShareContactCardResponse>(
    '/advisors/contact-card-shares',
    data,
  );
  return response.data;
}

/** The Service-Provider counterpart. `senderUserId` is the sending SP's user id. */
export async function shareContactCardAsProvider(
  senderUserId: string,
  data: ShareContactCardRequest,
): Promise<ShareContactCardResponse> {
  const response = await apiClient.post<ShareContactCardResponse>(
    `/service-providers/${senderUserId}/contact-card-shares`,
    data,
  );
  return response.data;
}
